import { useState } from "react";
import "./PublicRegister.css";
import Board from "@publicPortal/components/board/Board";
import { Stepper, Step, StepLabel, Stack, Box, Button, Grid, Alert } from "@mui/material";
import { useNavigate } from "react-router-dom";
import BigLogo from "@publicPortal/components/bigLogo/BigLogo";
import { Title } from "@shared/mirage/core/enums";
import FirstStepFields from "./FirstStepFields";
import SecondStepFields from "./SecondStepFields";
import ThirdStepFields from "./ThirdStepFields";
import validators, {
    IEmailValidatorResult,
    IMobilePhoneValidatorResult,
    IPasswordValidatorResult,
    ITwoPasswordsValidatorResult,
    ValidationResult,
} from "@shared/mirage/validators";
import { UserService } from "@publicPortal/services/UserService";
import ResultBoard from "@publicPortal/components/resultBoard/ResultBoard";
import { PublicPortalRoutingService } from "@publicPortal/services/PublicPortalRoutingService";
import { SignUpRequest, SignUpResult } from "@shared/mirage/core/contracts/User";
import { ISelectedAddressRecord } from "@mirage/components/addressBox/AddressAutocomplete";

const STEPS: RegisterSteps[] = ["Credentials", "Personal Information", "Contact Information"];

const getNumberOfSteps = () => {
    return STEPS.length - 1;
};

const Register = () => {
    const userService = new UserService();
    const pp = new PublicPortalRoutingService();
    const navigate = useNavigate();
    const [email, setEmail] = useState({ value: "", type: "email", validationResult: { passed: true } } as RegisterInputData);
    const [password, setPassword] = useState({ value: "", type: "password", showPassword: false, validationResult: { passed: true } } as RegisterInputData);
    const [passwordConfirm, setPasswordConfirm] = useState({ value: "", type: "password", showPassword: false, validationResult: { passed: true } } as RegisterInputData);
    const [title, setTitle] = useState({ value: "" as Title, type: "text", validationResult: { passed: true } } as RegisterUserTitle);
    const [firstName, setFirstName] = useState({ value: "", type: "text", validationResult: { passed: true } } as RegisterInputData);
    const [lastName, setLastName] = useState({ value: "", type: "text", validationResult: { passed: true } } as RegisterInputData);
    const [mobilePhone, setMobilePhone] = useState({ value: "", type: "text", validationResult: { passed: true } } as RegisterInputData);
    const [address, setAddress] = useState({ value: "", type: "text", validationResult: { passed: true } } as RegisterInputData);
    const [registrationSuccess, setRegistrationSuccess] = useState(false);
    const [alertData, setAlertData] = useState({ show: false, text: "" });
    const [currentStep, setStep] = useState(0);
    const [termsData, setTermsData] = useState({ checked: false, showError: false });
    const [customProperties, setCustomProperties] = useState({
        addressLine1: "",
        addressLine2: "",
        addressLine3: "",
        postTown: "",
        postCode: "",
        uprn: "",
    });

    const showPasswordHandler = (reference: "password" | "passwordConfirm", hidePassword?: true) => {
        if (reference === "password") {
            setPassword((previousState) => {
                return {
                    ...previousState,
                    showPassword: hidePassword ? false : !previousState.showPassword,
                };
            });
        } else {
            setPasswordConfirm((previousState) => {
                return {
                    ...previousState,
                    showPassword: hidePassword ? false : !previousState.showPassword,
                };
            });
        }
    };

    const handleTermsOnChange = () => {
        setTermsData((prev) => {
            return {
                checked: !prev.checked,
                showError: false,
            };
        });
    };

    const closeAlert = () => {
        setAlertData((previousState) => {
            return {
                ...previousState,
                show: false,
            };
        });
    };

    const handleNext = async () => {
        switch (currentStep) {
            case 0: {
                const emailValidationResult = validateInput("email");

                if (emailValidationResult.passed) {
                    const emailAlreadyExistsResult = await userService.checkEmailExists({ email: email.value });

                    if (emailAlreadyExistsResult.emailExists) {
                        emailValidationResult.passed = false;
                        emailValidationResult.error = "Email already exists";
                    }

                    handleInputValidation("email", emailValidationResult);
                }

                const passwordValidationResult = validateInput("password");

                if (passwordValidationResult.passed) {
                    const checkPasswordLengthResult = await userService.checkPasswordLength({ password: password.value });

                    if (checkPasswordLengthResult.exceedsMaxLength) {
                        passwordValidationResult.passed = false;
                        passwordValidationResult.error = `Password should not be longer than ${checkPasswordLengthResult.maxPasswordLength} characters`;
                    }

                    handleInputValidation("password", passwordValidationResult);
                }

                const passwordConfirmValidationResult = validateInput("passwordConfirm");
                handleInputValidation("passwordConfirm", passwordConfirmValidationResult);

                if (emailValidationResult.passed && passwordValidationResult.passed && passwordConfirmValidationResult.passed) {
                    setStep((currentStep) => (currentStep += 1));
                    showPasswordHandler("password", true);
                    showPasswordHandler("passwordConfirm", true);
                }
                break;
            }
            case 1: {
                const titleValidationResult = validateInput("title");
                handleInputValidation("title", titleValidationResult);

                const firstNameValidationResult = validateInput("firstName");
                handleInputValidation("firstName", firstNameValidationResult);

                const lastNameConfirmValidationResult = validateInput("lastName");
                handleInputValidation("lastName", lastNameConfirmValidationResult);

                if (titleValidationResult.passed && firstNameValidationResult.passed && lastNameConfirmValidationResult.passed) {
                    setStep((currentStep) => (currentStep += 1));
                }
                break;
            }
        }
    };

    const handleSignUp = async () => {
        const mobilePhoneNumberValidationResult = validateInput("mobilePhone");
        handleInputValidation("mobilePhone", mobilePhoneNumberValidationResult);

        const addressValidationResult = validateInput("address");
        handleInputValidation("address", addressValidationResult);

        if (mobilePhoneNumberValidationResult.passed && addressValidationResult.passed && termsData.checked) {
            const registrationData: SignUpRequest = {
                email: email.value,
                password: password.value,
                passwordConfirm: passwordConfirm.value,
                title: title.value as Title,
                firstName: firstName.value,
                lastName: lastName.value,
                address: address.value,
                mobilePhone: mobilePhone.value,
                custom_properties: JSON.stringify(customProperties),
            };
            const requestResult: SignUpResult = await userService.register(registrationData);

            if (requestResult.errorMsg) {
                if (requestResult.errorData?.length) {
                    requestResult.errorData.forEach((data: FieldValidationResult) => {
                        handleInputValidation(data.fieldName, { passed: false, error: data.errorMsg });
                    });
                }
                setAlertData({ show: true, text: requestResult.errorMsg });
            } else {
                setRegistrationSuccess(true);
            }
        } else if (!termsData.checked) {
            setTermsData((prev) => {
                return {
                    ...prev,
                    showError: true,
                };
            });
        }
    };

    const handleInputChange = (value: string, reference: RegistrationFieldReferences) => {
        switch (reference) {
            case "email":
                setEmail((previousState) => {
                    const emailValidationResult: IEmailValidatorResult = validators.EmailValidator(value);
                    return {
                        ...previousState,
                        value: value,
                        validationResult: emailValidationResult,
                    };
                });

                break;
            case "password":
                setPassword((previousState) => {
                    const passwordValidationResult: IPasswordValidatorResult = validators.PasswordValidator(value);
                    return {
                        ...previousState,
                        value: value,
                        validationResult: passwordValidationResult,
                    };
                });
                break;
            case "passwordConfirm":
                setPasswordConfirm((previousState) => {
                    const passwordComplianceValidationResult: ITwoPasswordsValidatorResult = validators.CompareTwoPasswords(password.value, value);
                    return {
                        ...previousState,
                        value: value,
                        validationResult: passwordComplianceValidationResult,
                    };
                });
                break;
            case "title":
                setTitle((previousState) => {
                    const titleValidationResult: ValidationResult = validators.IsStringNotEmptyValidator(value, "Enter title");
                    return {
                        ...previousState,
                        value: value as Title,
                        validationResult: titleValidationResult,
                    };
                });
                break;
            case "firstName":
                setFirstName((previousState) => {
                    const firstNameValidationResult: ValidationResult = validators.IsStringNotEmptyValidator(value, "Enter First Name");
                    return {
                        ...previousState,
                        value: value,
                        validationResult: firstNameValidationResult,
                    };
                });
                break;
            case "lastName":
                setLastName((previousState) => {
                    const lastNameValidationResult: ValidationResult = validators.IsStringNotEmptyValidator(value, "Enter Last Name");
                    return {
                        ...previousState,
                        value: value,
                        validationResult: lastNameValidationResult,
                    };
                });
                break;
            case "mobilePhone":
                setMobilePhone((previousState) => {
                    const phoneValidationResult: IMobilePhoneValidatorResult = validators.MobilePhoneValidator(value);
                    return {
                        ...previousState,
                        value: value,
                        validationResult: phoneValidationResult,
                    };
                });
                break;
        }
    };

    const addressBoxHandler = ({ address, addressDetails, addressLines }: ISelectedAddressRecord) => {
        setAddress((previousState) => {
            const validationResult: ValidationResult = validators.IsStringNotEmptyValidator(address, "Enter Address");
            return {
                ...previousState,
                value: address,
                validationResult,
            };
        });
        setCustomProperties((previousState) => ({
            ...previousState,
            addressLine1: addressLines.line1,
            addressLine2: addressLines.line2,
            addressLine3: addressLines.line3,
            postCode: addressDetails.POSTCODE,
            postTown: addressDetails.TOWN_NAME,
            uprn: addressDetails.UPRN,
        }));
    };

    const handleInputBlur = (reference: RegistrationFieldReferences) => {
        const validationResult = validateInput(reference);
        handleInputValidation(reference, validationResult);
    };

    const validateInput = (reference: RegistrationFieldReferences) => {
        switch (reference) {
            case "email":
                return validators.EmailValidator(email.value);
            case "password":
                return validators.PasswordValidator(password.value);
            case "passwordConfirm":
                return validators.CompareTwoPasswords(password.value, passwordConfirm.value);
            case "title":
                return validators.IsStringNotEmptyValidator(title.value, "Enter Title");
            case "firstName":
                return validators.IsStringNotEmptyValidator(firstName.value, "Enter First Name");
            case "lastName":
                return validators.IsStringNotEmptyValidator(lastName.value, "Enter Last Name");
            case "mobilePhone":
                return validators.MobilePhoneValidator(mobilePhone.value);
            case "address":
                return validators.IsStringNotEmptyValidator(address.value, "Enter Address");
            default:
                throw Error(`Bad reference for validation - ${reference}`);
        }
    };

    const handleInputValidation = (reference: RegistrationFieldReferences, validationResult: { passed?: boolean; error?: string }) => {
        switch (reference) {
            case "email":
                setEmail((previousState) => {
                    return {
                        ...previousState,
                        validationResult: validationResult,
                    };
                });
                break;
            case "password":
                setPassword((previousState) => {
                    return {
                        ...previousState,
                        validationResult: validationResult,
                    };
                });
                break;
            case "passwordConfirm":
                setPasswordConfirm((previousState) => {
                    return {
                        ...previousState,
                        validationResult: validationResult,
                    };
                });
                break;
            case "title":
                setTitle((previousState) => {
                    return {
                        ...previousState,
                        validationResult: validationResult,
                    };
                });
                break;
            case "firstName":
                setFirstName((previousState) => {
                    return {
                        ...previousState,
                        validationResult: validationResult,
                    };
                });
                break;
            case "lastName":
                setLastName((previousState) => {
                    return {
                        ...previousState,
                        validationResult: validationResult,
                    };
                });
                break;
            case "mobilePhone":
                setMobilePhone((previousState) => {
                    return {
                        ...previousState,
                        validationResult: validationResult,
                    };
                });
                break;
            case "address":
                setAddress((previousState) => {
                    return {
                        ...previousState,
                        validationResult: validationResult,
                    };
                });
                break;
        }
    };

    const getFields = () => {
        switch (currentStep) {
            case 0:
                return (
                    <FirstStepFields
                        emailData={email}
                        passwordData={password}
                        passwordConfirmData={passwordConfirm}
                        onChangeHandler={handleInputChange}
                        onBlurHandler={handleInputBlur}
                        showPasswordHandler={showPasswordHandler}
                    />
                );
            case 1:
                return <SecondStepFields titleData={title} firstNameData={firstName} lastNameData={lastName} onChangeHandler={handleInputChange} onBlurHandler={handleInputBlur} />;
            case 2:
                return (
                    <ThirdStepFields
                        termsData={termsData}
                        termsOnChange={handleTermsOnChange}
                        mobilePhoneData={mobilePhone}
                        addressData={address}
                        onChangeHandler={handleInputChange}
                        onBlurHandler={handleInputBlur}
                        addressBoxHandler={addressBoxHandler}
                    />
                );
            default:
                return null;
        }
    };

    const getButtons = () => {
        return (
            <Grid container justifyContent="center" alignItems="center" sx={{ marginTop: "2rem" }}>
                <Grid item xs={3} justifyContent="center">
                    {currentStep > 0 ? (
                        <Button onClick={() => setStep((currentStep) => (currentStep -= 1))}>Back</Button>
                    ) : (
                        <Button onClick={() => navigate(pp.LoginRoute())}>Sign In Instead</Button>
                    )}
                </Grid>
                <Grid item xs={3} textAlign="center">
                    {currentStep === getNumberOfSteps() ? (
                        <Button variant="contained" onClick={async () => await handleSignUp()}>
                            Sign Up
                        </Button>
                    ) : (
                        <Button variant="contained" onClick={handleNext}>
                            Next
                        </Button>
                    )}
                </Grid>
            </Grid>
        );
    };

    const getRegistrationFormBoard = () => {
        return (
            <>
                <Board key={"register-board-key"} headerText={"Create Account"} size="small">
                    <Stepper activeStep={currentStep} alternativeLabel>
                        {STEPS.map((label) => {
                            return (
                                <Step key={label}>
                                    <StepLabel sx={{ marginTop: "0.5rem" }}>{label}</StepLabel>
                                </Step>
                            );
                        })}
                    </Stepper>
                    <form className="registration-form">
                        <Box>
                            <Stack spacing={4} justifyContent={"center"} alignItems={"center"}>
                                {getFields()}
                                {getButtons()}
                            </Stack>
                        </Box>
                    </form>
                </Board>
            </>
        );
    };

    const getRegistrationResultBoard = () => {
        return (
            <ResultBoard
                loading={false}
                success={true}
                headerText="Create Account"
                buttonLabel="Login"
                buttonNavTo={pp.LoginRoute()}
                message="Registration confirmation email has been sent to: "
            >
                <b>{email.value}</b>
            </ResultBoard>
        );
    };

    return (
        <>
            <BigLogo />
            {registrationSuccess ? getRegistrationResultBoard() : getRegistrationFormBoard()}
            <Box position="absolute" bottom="20px" left="20px" width="40%">
                {alertData.show ? (
                    <Alert severity="error" onClose={closeAlert}>
                        {alertData.text}
                    </Alert>
                ) : null}
            </Box>
        </>
    );
};

type RegistrationFieldReferences = keyof SignUpRequest;

type RegisterSteps = "Credentials" | "Personal Information" | "Contact Information";

interface RegisterInputData {
    value: string | Title;
    type: string;
    showPassword?: boolean;
    validationResult: IPasswordValidatorResult;
}

interface RegisterUserTitle {
    value: Title;
    type: string;
    validationResult: {
        passed?: boolean;
        error?: string;
    };
}

interface FieldValidationResult {
    fieldName: keyof SignUpRequest;
    errorMsg: string | undefined;
}

export default Register;
export type { RegisterInputData, RegistrationFieldReferences };
